import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Shield, Clock, MessageSquare, ArrowLeft, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalLoginPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone_number: ''
  });

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset');
    if (token) {
      setResetToken(token);
      setShowReset(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/client/login' : '/api/client/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await axios.post(`${API_URL}${endpoint}`, payload, { withCredentials: true });
      
      localStorage.setItem('client_token', response.data.token);
      localStorage.setItem('client_user', JSON.stringify(response.data));
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/portal/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(typeof message === 'string' ? message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/client/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/client/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success('Password reset! You can now log in.');
      setShowReset(false);
      setResetToken('');
      // Clean URL
      window.history.replaceState({}, '', '/portal/login');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Reset failed. The link may have expired.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Shield, text: 'Secure, encrypted portal' },
    { icon: Clock, text: '24/7 access to your application' },
    { icon: MessageSquare, text: 'Direct advisor messaging' }
  ];

  return (
    <div className="min-h-screen flex" data-testid="portal-login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full border-2 border-white"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full border border-white"></div>
        </div>
        
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2 mb-16">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl font-['Outfit']">Q</span>
            </div>
            <span className="text-xl font-bold font-['Outfit']">QuickFinance</span>
          </Link>
          
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 font-['Outfit'] leading-tight">
            Your Journey to<br />
            <span className="text-blue-400">Business Growth</span>
          </h1>
          
          <p className="text-slate-300 text-lg mb-12 max-w-md">
            Access your personal dashboard to track your application, upload documents, and communicate with your advisor.
          </p>
          
          <div className="space-y-4">
            {features.map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <feature.icon className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-slate-300">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
        
        <p className="text-slate-500 text-sm relative z-10">
          © {new Date().getFullYear()} QuickFinance.io. All rights reserved.
        </p>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="p-6">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-700 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Password Reset Form */}
            {showReset ? (
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit'] mb-2">Set New Password</h2>
                <p className="text-slate-600 mb-6">Enter your new password below.</p>
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div>
                    <Label htmlFor="new_password" className="text-sm font-medium text-slate-700">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      required
                      minLength={8}
                      className="mt-1.5 h-12"
                      data-testid="input-reset-password"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white h-12" data-testid="submit-reset-password">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Set New Password
                  </Button>
                </form>
              </div>
            ) : showForgot ? (
              /* Forgot Password Form */
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit'] mb-2">Forgot Password</h2>
                {forgotSent ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-blue-700" />
                    </div>
                    <p className="text-slate-600 mb-6">If an account exists for <strong>{forgotEmail}</strong>, we've sent a password reset link.</p>
                    <Button variant="outline" onClick={() => { setShowForgot(false); setForgotSent(false); }} data-testid="back-to-login-btn">
                      Back to Login
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-slate-600 mb-6">Enter your email and we'll send you a reset link.</p>
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                      <div>
                        <Label htmlFor="forgot_email" className="text-sm font-medium text-slate-700">Email Address</Label>
                        <Input
                          id="forgot_email"
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="Enter your email"
                          required
                          className="mt-1.5 h-12"
                          data-testid="input-forgot-email"
                        />
                      </div>
                      <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white h-12" data-testid="submit-forgot-password">
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Send Reset Link
                      </Button>
                    </form>
                    <p className="text-center mt-4">
                      <button onClick={() => setShowForgot(false)} className="text-blue-700 font-medium hover:underline text-sm" data-testid="back-to-login-link">
                        Back to Login
                      </button>
                    </p>
                  </>
                )}
              </div>
            ) : (
              /* Login/Register Form */
              <div>
              <div className="mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']">
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-slate-600 mt-2">
                {isLogin ? 'Sign in to access your client portal' : 'Register to start your funding journey'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="full_name" className="text-sm font-medium text-slate-700">Full Name</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                      placeholder="Enter your full name"
                      required
                      className="mt-1.5 h-12"
                      data-testid="input-register-name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone_number" className="text-sm font-medium text-slate-700">Phone Number</Label>
                    <Input
                      id="phone_number"
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                      placeholder="Enter your phone number"
                      required
                      className="mt-1.5 h-12"
                      data-testid="input-register-phone"
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter your email"
                  required
                  className="mt-1.5 h-12"
                  data-testid="input-portal-email"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={isLogin ? 'Enter your password' : 'Create a password (min 8 chars)'}
                    required
                    minLength={isLogin ? undefined : 8}
                    className="h-12 pr-12"
                    data-testid="input-portal-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white h-12 text-base font-medium"
                data-testid="submit-portal-auth"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            {isLogin && (
              <p className="text-center mt-3">
                <button onClick={() => setShowForgot(true)} className="text-sm text-slate-500 hover:text-blue-700 hover:underline" data-testid="forgot-password-link">
                  Forgot your password?
                </button>
              </p>
            )}

            <p className="text-center text-slate-600 mt-4">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-blue-700 font-medium hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Register' : 'Sign In'}
              </button>
            </p>
            </div>
            )}

            {/* Mobile branding */}
            <div className="lg:hidden mt-12 pt-8 border-t border-slate-200">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold font-['Outfit']">Q</span>
                </div>
                <span className="font-bold text-slate-900 font-['Outfit']">QuickFinance</span>
              </div>
              <div className="flex justify-center gap-6 text-sm text-slate-500">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
